import { useCallback } from 'react'
import { useRecoilValue } from 'recoil'
import {
  processImage,
  createDefaultImageReader,
  createDefaultImageWriter,
  PinturaDefaultImageWriterResult,
} from '@messenger/pintura'
import { caseAtom } from '@/atoms'
import { getPresignedUploadUrl } from '@/api/caseImages'
import { uploadToPresignedUrl } from '@messenger/utils'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import {
  createCutoutImageReader,
  withCorsQuery,
} from '@/utils/editor/cutoutImageReader'
import type {
  ColorMatrix,
  GetCaseByIdNormalized as Case,
  ImageItem,
} from '@messenger/types'

// Matches the structure of ImageItem['image']['edits']['colorMatrix']
// Each property is a 20-element color transformation matrix
type ColorMatrixEdits = {
  filter?: ColorMatrix
  brightness?: ColorMatrix
  contrast?: ColorMatrix
  saturation?: ColorMatrix
  exposure?: ColorMatrix
  temperature?: ColorMatrix
}

type ImageEditOptions = {
  contrast?: number
  saturation?: number
  gamma?: number
  // Object containing named color matrices (e.g., { filter: ColorMatrix, brightness: ColorMatrix })
  // NOT a raw ColorMatrix array — use this for direct colorMatrix overrides
  colorMatrix?: ColorMatrixEdits
  reset?: boolean
}

type ApplyImageEditParams = {
  imageUrl: string
  /**
   * Set when `imageUrl` is an accepted AI cutout: the photo it replaces. The
   * cutout is then read at that photo's size, where the existing edits were
   * measured, and written as a PNG so its transparency is kept.
   */
  cutoutOfPhotoUrl?: string
  existingEdits?: ImageItem['image']['edits']
  // New edits to apply
  edits: ImageEditOptions
}

type ApplyImageEditSuccessResult = {
  success: true
  editedUrl: string
  imageState: ImageItem['image']['edits']
}

type ApplyImageEditFailureResult = {
  success: false
  error: string
}

type ApplyImageEditResult =
  | ApplyImageEditSuccessResult
  | ApplyImageEditFailureResult

// Helper to create contrast color matrix for Pintura
// Contrast range: Typically -1 to 1 (decimal values like 0.3 for 30% increase)
// Increases or decreases the difference between light and dark values
const createContrastMatrix = (value: number): ColorMatrix => {
  // Value is already in decimal form (e.g., 0.3 for 30% increase)
  // Convert to multiplier (e.g., 0.3 becomes 1.3)
  const multiplier = 1 + value
  // Offset to keep middle gray at same level
  const offset = 0.5 * (1 - multiplier)
  return [
    multiplier,
    0,
    0,
    0,
    offset,
    0,
    multiplier,
    0,
    0,
    offset,
    0,
    0,
    multiplier,
    0,
    offset,
    0,
    0,
    0,
    1,
    0,
  ]
}

// Helper to create saturation color matrix for Pintura
// Saturation range: Typically -1 to 1 (decimal values like -0.15 for 15% decrease)
// Uses luminance weights for RGB to grayscale conversion
const createSaturationMatrix = (value: number): ColorMatrix => {
  // Value is already in decimal form (e.g., -0.15 for 15% decrease)
  const saturationValue = 1 + value

  // Luminance weights used internally by Pintura (matches finetune.saturation matrix math)
  const luminanceRed = 0.213
  const luminanceGreen = 0.715
  const luminanceBlue = 0.072

  // Calculate matrix values
  // When saturation = 1, we get identity matrix (no change)
  // When saturation = 0, we get grayscale
  // When saturation > 1, colors are more vivid
  const saturationComplement = 1 - saturationValue
  const redValue = luminanceRed * saturationComplement + saturationValue
  const greenValue = luminanceGreen * saturationComplement + saturationValue
  const blueValue = luminanceBlue * saturationComplement + saturationValue
  const redFromGreen = luminanceRed * saturationComplement
  const redFromBlue = luminanceRed * saturationComplement
  const greenFromRed = luminanceGreen * saturationComplement
  const greenFromBlue = luminanceGreen * saturationComplement
  const blueFromRed = luminanceBlue * saturationComplement
  const blueFromGreen = luminanceBlue * saturationComplement

  return [
    redValue,
    greenFromRed,
    blueFromRed,
    0,
    0,
    redFromGreen,
    greenValue,
    blueFromGreen,
    0,
    0,
    redFromBlue,
    greenFromBlue,
    blueValue,
    0,
    0,
    0,
    0,
    0,
    1,
    0,
  ]
}

// Helper to clamp numeric values safely within a min/max range
const clampNumber = (value: number, min: number, max: number): number => {
  // Clamp lower bound first, then upper bound to avoid NaN propagation
  return Math.min(Math.max(value, min), max)
}

// Converts a desired gamma percentage (e.g., 15 for +15%) into Pintura's internal gamma value
export const convertGammaPercentageToInternalValue = (
  desiredGammaPercentage: number,
): number => {
  // Normalize the requested percentage to a 0-1 or -1-0 range
  const normalizedPercentage = desiredGammaPercentage / 100

  // Positive adjustments use the high-end gamma scaling (baseline 1.0 up to 4.0)
  const convertedPositiveGammaValue = 1 + 3 * normalizedPercentage

  // Negative adjustments use the low-end gamma scaling (down to Pintura minimum 0.15)
  const convertedNegativeGammaValue = 0.15 + 0.85 * (normalizedPercentage + 1)

  const rawGammaValue =
    desiredGammaPercentage >= 0
      ? convertedPositiveGammaValue
      : convertedNegativeGammaValue

  // Pintura accepts gamma values between 0.15 and 4.0
  return clampNumber(rawGammaValue, 0.15, 4)
}

// Converts a desired saturation percentage (e.g., -15 for -15%) into the offset used by Pintura matrices
export const convertSaturationPercentageToOffset = (
  desiredSaturationPercentage: number,
): number => {
  // Pintura saturation accepts offsets between -1 (fully desaturated) and +1 (double saturation)
  const normalizedSaturationOffset = desiredSaturationPercentage / 100
  return clampNumber(normalizedSaturationOffset, -1, 1)
}

// Removes previous color-related adjustments so presets can start from a clean slate
export const removeProgrammaticColorAdjustmentsFromImageEdits = (
  existingEdits: ImageItem['image']['edits'] | undefined,
): ImageItem['image']['edits'] => {
  if (!existingEdits) {
    return {}
  }

  const {
    colorMatrix: _colorMatrix,
    gamma: _gamma,
    vignette: _vignette,
    noise: _noise,
    convolutionMatrix: _convolutionMatrix,
    ...remainingEdits
  } = existingEdits

  return { ...remainingEdits }
}

export const hasColorAdjustments = (
  imageEdits: ImageItem['image']['edits'],
): boolean => {
  return (
    !!imageEdits?.colorMatrix && Object.keys(imageEdits.colorMatrix).length > 0
  )
}

export const useProgrammaticImageEdit = () => {
  const handleError = useErrorHandler('useProgrammaticImageEdit')
  const _case = useRecoilValue(caseAtom) as Case

  const applyImageEdit = useCallback(
    async (params: ApplyImageEditParams): Promise<ApplyImageEditResult> => {
      const { imageUrl, cutoutOfPhotoUrl, existingEdits = {}, edits } = params

      try {
        // Validate preconditions - return failure result instead of throwing
        // This ensures consistent error handling through handleError
        const caseId = _case?.id
        if (!caseId) {
          const errorMessage = 'Case ID is required'
          handleError(errorMessage, new Error(errorMessage))
          return {
            success: false,
            error: errorMessage,
          }
        }

        if (!imageUrl) {
          const errorMessage = 'Image URL is required'
          handleError(errorMessage, new Error(errorMessage))
          return {
            success: false,
            error: errorMessage,
          }
        }
        // Build imageState - if resetting, DON'T include colorMatrix or gamma from existingEdits
        const imageState: ImageItem['image']['edits'] = {
          // Required base properties (Pintura needs these)
          cropLimitToImage: existingEdits.cropLimitToImage ?? true,
          cropMinSize: existingEdits.cropMinSize ?? { width: 1, height: 1 },
          cropMaxSize: existingEdits.cropMaxSize ?? {
            width: 32768,
            height: 32768,
          },
          flipX: existingEdits.flipX ?? false,
          flipY: existingEdits.flipY ?? false,
          rotation: existingEdits.rotation ?? 0,
          crop: existingEdits.crop,
          backgroundColor: existingEdits.backgroundColor ?? [0, 0, 0, 0],
          redaction: existingEdits.redaction ?? [],
          annotation: existingEdits.annotation ?? [],
          decoration: existingEdits.decoration ?? [],
        }

        if (edits.reset) {
          // Don't add colorMatrix or gamma at all - they'll default to baseline
        } else {
          const newColorMatrix: NonNullable<
            ImageItem['image']['edits']['colorMatrix']
          > = existingEdits.colorMatrix ? { ...existingEdits.colorMatrix } : {}

          const contrastSpecified = edits.contrast !== undefined
          if (contrastSpecified) {
            // Contrast values are decimals (e.g., 0.3 for 30% increase)
            // We set them directly rather than accumulating
            newColorMatrix.contrast = createContrastMatrix(edits.contrast!)
          }

          const saturationSpecified = edits.saturation !== undefined
          if (saturationSpecified) {
            // Saturation values are decimals (e.g., -0.15 for 15% decrease)
            // We set them directly rather than accumulating
            newColorMatrix.saturation = createSaturationMatrix(
              edits.saturation!,
            )
          }

          const colorMatrixSpecified = edits.colorMatrix !== undefined
          if (colorMatrixSpecified) {
            Object.assign(newColorMatrix, edits.colorMatrix)
          }

          const colorMatrixHasProperties =
            Object.keys(newColorMatrix).length > 0
          if (colorMatrixHasProperties) {
            imageState.colorMatrix = newColorMatrix
          }

          const gammaSpecified = edits.gamma !== undefined
          if (gammaSpecified) {
            imageState.gamma = edits.gamma
          }
        }

        let result: PinturaDefaultImageWriterResult | undefined
        try {
          result = (await Promise.race([
            processImage(withCorsQuery(imageUrl), {
              imageState,
              imageReader: cutoutOfPhotoUrl
                ? createCutoutImageReader(cutoutOfPhotoUrl)
                : createDefaultImageReader(),
              imageWriter: createDefaultImageWriter(
                cutoutOfPhotoUrl ? { mimeType: 'image/png' } : {},
              ),
            }),
            new Promise((_, reject) =>
              setTimeout(
                () => reject(new Error('processImage timeout after 15s')),
                15000,
              ),
            ),
          ])) as PinturaDefaultImageWriterResult
        } catch (processError) {
          console.error('processError', processError)
          throw processError
        }

        const failedToProcess = !result || !result.dest || !result.imageState
        if (failedToProcess) {
          throw new Error('Failed to process image')
        }

        const blob = result.dest as Blob
        const contentType = blob.type || 'image/png'
        const { url: presignedUrl, s3Key } = await getPresignedUploadUrl(
          caseId,
          contentType,
        )
        const file = new File([blob], 'programmatic-edit', {
          type: contentType,
        })
        await uploadToPresignedUrl(presignedUrl, file)
        const bucketUrl = (
          process.env.REACT_APP_S3BUCKET_CASE_IMAGES || ''
        ).replace(/\/+$/, '')
        if (!bucketUrl) {
          throw new Error('REACT_APP_S3BUCKET_CASE_IMAGES is not configured')
        }
        const editedUrl = `${bucketUrl}/${s3Key}`

        return {
          success: true,
          editedUrl,
          imageState: result.imageState,
        }
      } catch (error: unknown) {
        handleError(
          error instanceof Error ? error.message : 'Unknown error',
          error as Error,
        )
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error',
        }
      }
    },
    [_case?.id, handleError],
  )

  return {
    applyImageEdit,
  }
}
